import { ENV } from '../../config/runtimeEnv';

import { Breadcrumb, ReportFile } from "../../types/file/FileTypes";
import type { UserUuid } from "../../types/identity";
import { http } from "../../lib/http/client";
import { fileNameFromContentDisposition } from "../../utils/contentDisposition";

const API_BASE_URL = ENV.VITE_FILE_SERVER;

export const uploadFiles = async (files: File[], userId: UserUuid, parentId?: string) => {
	try {
		const formData = new FormData();
		files.forEach(file => {
			formData.append("files[]", file);
		});
		formData.append("user_id", userId.toString());
		if (parentId) {
			formData.append("parent_id", parentId);
		}

		const response = await http.post(`${API_BASE_URL}/files`, formData);

		return response.data;
	} catch (error) {
		throw new Error(`Error uploading files: ${error}`);
	}
};

export const createFolder = async (userId: UserUuid, folderName: string, parentId?: string) => {
	try {
		const response = await http.post(`${API_BASE_URL}/files/folder`, {
			user_id: userId,
			folder_name: folderName,
			parent_id: parentId,
		});
		return response.data;
	} catch (error) {
		throw new Error(`Error creating folder: ${error}`);
	}
};

export const moveItem = async (fileId: string, userId: UserUuid, newParentId?: string) => {
	try {
		const response = await http.put(`${API_BASE_URL}/files/move`, {
			file_id: fileId,
			user_id: userId,
			new_parent_id: newParentId,
		});
		return response.data;
	} catch (error) {
		throw new Error(`Error moving item: ${error}`);
	}
};

export const getBreadcrumbs = async (fileId: string, userId: UserUuid): Promise<Breadcrumb[]> => {
	try {
		const response = await http.post(`${API_BASE_URL}/files/breadcrumbs`, {
			file_id: fileId,
			user_id: userId,
		});
		return response.data;
	} catch (error) {
		throw new Error(`Error getting breadcrumbs: ${error}`);
	}
};

export const listUserFiles = async (userId: UserUuid, parentId?: string) => {
	try {
		const params = parentId ? { parent_id: parentId } : { parent_id: 'root' };
		const response = await http.get(`${API_BASE_URL}/files/user/${userId}`, {
			params,
		});
		return response.data;
	} catch (error) {
		throw new Error(`Error listing files: ${error}`);
	}
};

export const updateClinicalOnly = async (fileId: string, clinicalOnly: boolean, patientId: UserUuid) => {
	try {
		const response = await http.put(`${API_BASE_URL}/files/clinical-only`,
			{ file_id: fileId, user_id: patientId, clinical_only: clinicalOnly }
		);
		return response.data;
	} catch (error) {
		throw new Error(`Error updating clinical only status: ${error}`);
	}
};

export const downloadFile = async (userId: UserUuid, fileId: string, onProgress?: (progressEvent: any) => void) => {
	try {
		const response = await http.post(`${API_BASE_URL}/files/download`,
			{ user_id: userId, file_id: fileId },
			{
				responseType: "blob",
				onDownloadProgress: onProgress,
			}
		);

		const contentType = (response.headers["content-type"] as string) || "application/octet-stream";
		const blob = new Blob([response.data], { type: contentType });
		const fileURL = window.URL.createObjectURL(blob);
		const fileLink = document.createElement("a");
		fileLink.href = fileURL;

		const fileName = fileNameFromContentDisposition(response.headers["content-disposition"], "downloaded-file");

		fileLink.setAttribute("download", fileName);
		document.body.appendChild(fileLink);
		fileLink.click();
		fileLink.remove();
		window.URL.revokeObjectURL(fileURL);

		return { fileName, success: true };
	} catch (error) {
		throw new Error(`Error downloading file: ${error}`);
	}
};

/**
 * View a patient-owned file inline (blob URL), for the CoA review document pane. Hits the
 * same /files/download endpoint downloadFile() above uses, mirroring viewReport()'s
 * blob-to-object-URL shape — but keeps the blob's real content-type from the response
 * headers rather than assuming a PDF, since a CoA can legitimately be a JPEG/PNG.
 */
export const viewPatientFile = async (
	userId: UserUuid,
	fileId: string
): Promise<{ url: string; contentType: string }> => {
	try {
		const response = await http.post(`${API_BASE_URL}/files/download`,
			{ user_id: userId, file_id: fileId },
			{
				responseType: "blob",
			}
		);

		const contentType = (response.headers["content-type"] as string) || "application/octet-stream";
		const blob = new Blob([response.data], { type: contentType });
		const url = window.URL.createObjectURL(blob);
		return { url, contentType };
	} catch (error) {
		throw new Error(`Error viewing file: ${error}`);
	}
};

export const deleteFile = async (fileId: string, userId: UserUuid) => {
	try {
		const response = await http.delete(`${API_BASE_URL}/files`, {
			data: { user_id: userId, file_id: fileId },
		});
		return response.data;
	} catch (error) {
		throw new Error(`Error deleting file: ${error}`);
	}
};

// ==================== REPORTS ====================

export const uploadReport = async (file: File, userId: UserUuid, sampleId: string) => {
	try {
		const formData = new FormData();
		formData.append("file", file);
		formData.append("patient_id", userId.toString());
		formData.append("sample_id", sampleId);

		const response = await http.post(`${API_BASE_URL}/reports/upload`, formData);

		return response.data;
	} catch (error) {
		throw new Error(`Error uploading file: ${error}`);
	}
};

export const listPatientReports = async (patientId: UserUuid, sampleId: string): Promise<ReportFile[]> => {
	try {
		const response = await http.get(`${API_BASE_URL}/reports/patient/${patientId}/${sampleId}`);
		return response.data;
	} catch (error) {
		throw new Error(`Error fetching reports: ${error}`);
	}
};

export const updateClinicalOnlyReport = async (fileId: string, clinicalOnly: boolean) => {
	try {
		const response = await http.put(`${API_BASE_URL}/reports/clinical-only`,
			{
				file_id: fileId,
				clinical_only: clinicalOnly,
			}
		);

		return response.data;
	} catch (error) {
		throw new Error(`Error updating clinical only status: ${error}`);
	}
}

export const downloadReport = async (
	patientId: UserUuid,
	fileId: string,
	onProgress?: (progressEvent: any) => void
) => {
	try {
		const response = await http.post(
			`${API_BASE_URL}/reports/download`,
			{
				patient_id: patientId,
				file_id: fileId,
			},
			{
				responseType: "blob",
				onDownloadProgress: onProgress,
			}
		);

		const contentType = (response.headers["content-type"] as string) || "application/octet-stream";
		const blob = new Blob([response.data], { type: contentType });
		const fileURL = window.URL.createObjectURL(blob);
		const fileLink = document.createElement("a");
		fileLink.href = fileURL;

		const fileName = fileNameFromContentDisposition(response.headers["content-disposition"], "downloaded-file");

		fileLink.setAttribute("download", fileName);
		document.body.appendChild(fileLink);
		fileLink.click();
		fileLink.remove();
		window.URL.revokeObjectURL(fileURL);

		return { fileName, success: true };
	} catch (error) {
		throw new Error(`Error downloading file: ${error}`);
	}
};

/**
 * Download the original uploaded blood-test report PDF (stored after analysis).
 */
export const downloadBloodTestReport = async (
	patientId: UserUuid,
	fileId: string,
	onProgress?: (progressEvent: any) => void
) => {
	try {
		const response = await http.post(
			`${API_BASE_URL}/blood-test-report/download`,
			{
				patient_id: patientId,
				file_id: fileId,
			},
			{
				responseType: "blob",
				onDownloadProgress: onProgress,
			}
		);

		const contentType = (response.headers["content-type"] as string) || "application/pdf";
		const blob = new Blob([response.data], { type: contentType });
		const fileURL = window.URL.createObjectURL(blob);
		const fileLink = document.createElement("a");
		fileLink.href = fileURL;

		const fileName = fileNameFromContentDisposition(response.headers["content-disposition"], "blood-test-report.pdf");

		fileLink.setAttribute("download", fileName);
		document.body.appendChild(fileLink);
		fileLink.click();
		fileLink.remove();
		window.URL.revokeObjectURL(fileURL);

		return { fileName, success: true };
	} catch (error) {
		throw new Error(`Error downloading blood test report: ${error}`);
	}
};

/**
 * View a PDF report inline (returns blob URL)
 */
export const viewReport = async (patientId: UserUuid, fileId: string): Promise<string> => {
	try {
		const response = await http.post(
			`${API_BASE_URL}/reports/view`,
			{
				patient_id: patientId,
				file_id: fileId,
			},
			{
				responseType: "blob",
			}
		);

		const blob = new Blob([response.data], { type: 'application/pdf' });
		return window.URL.createObjectURL(blob);
	} catch (error) {
		throw new Error(`Error viewing report: ${error}`);
	}
};

export const deleteReport = async (fileId: string, patientId: UserUuid) => {
	try {
		const response = await http.delete(`${API_BASE_URL}/reports/delete`, {
			data: {
				patient_id: patientId,
				file_id: fileId
			},
		});

		return response.data;
	} catch (error) {
		throw new Error(`Error deleting report: ${error}`);
	}
};

export const downloadSampleResult = async (userId: UserUuid, fileId: string) => {
	try {
		const response = await http.post(`${API_BASE_URL}/sample-results/download`, {
			user_id: userId,
			file_id: fileId,
		}, {
			responseType: "blob",
		});

		const contentType = (response.headers["content-type"] as string) || "application/octet-stream";
		const blob = new Blob([response.data], { type: contentType });
		const fileURL = window.URL.createObjectURL(blob);
		const fileLink = document.createElement("a");
		fileLink.href = fileURL;

		const fileName = fileNameFromContentDisposition(response.headers["content-disposition"], "downloaded-file");

		fileLink.setAttribute("download", fileName);
		document.body.appendChild(fileLink);
		fileLink.click();
		fileLink.remove();
		window.URL.revokeObjectURL(fileURL);

		return { fileName, success: true };
	} catch (error) {
		throw new Error(`Error downloading sample result: ${error}`);
	}
}